import React, { useState, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import fr from 'facet-rest';
import { makeStyles } from '@material-ui/core';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import Typography from '@material-ui/core/Typography';
import EditIcon from '@material-ui/icons/Edit';
import IconButton from '@material-ui/core/IconButton';
import { openModal } from 'Helpers/ModalHelper';
import { buildModalQuery } from 'Consts/ModalQueries';
import modalConsts from 'Consts/ModalNames';
import { currency, addSpacesAndCapitalize } from 'Helpers/Formats';
import PropTypes from 'prop-types';
import { refreshAccounts } from 'Components/UI/Snapshot/helpers';
import {
  REAL_ASSET_QUERY_VALUE_ID,
  REAL_ASSET_QUERY_VALUE_TYPE,
  REAL_ASSET_QUERY_VALUE_WITH_DELETE,
} from 'Consts/RealAssets';
import AccountStatus from 'Components/UI/AccountStatus';
import { useRefreshAccounts, dispatchAccountsRefresh } from 'Hooks/useRefreshEvent';
import { logicalBalance, displayName } from 'Helpers/Account';
import { AddRealAssetButton } from 'Components/UI/Snapshot/AddRealAssetButton';
import { openURLInNewTab } from 'Helpers/Navigation';

const useStyles = makeStyles(theme => ({
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerRow: {
    borderBottom: '1px solid',
    borderBottomColor: theme.palette.facet.lightGray2,
    color: theme.palette.facet.primaryBlue,
    fontWeight: 'bold',
  },
  darkRow: {
    backgroundColor: theme.palette.facet.lightGray,
  },
  footerRow: {
    borderTop: '1px solid',
    borderTopColor: theme.palette.facet.lightGray2,
  },
  name: {
    display: 'flex',
    justifyContent: 'space-between',
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    width: 200,
  },
  balance: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    width: 150,
    textAlign: 'right',
  },
  smaller: { padding: theme.spacing(0.5) },
  liability: {
    color: theme.palette.facet.red,
  },
  label: {
    marginBottom: theme.spacing(2),
  },
  titleRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  accountLink: {
    alignSelf: 'center',
    color: theme.palette.secondary.main,
  },
}));

export default function BalanceSheet() {
  const classes = useStyles();
  const { householdResource } = useSimpleHousehold();
  const [liabilities, setLiabilities] = useState([]);
  const [investmentAssets, setInvestmentAssets] = useState([]);
  const [realAssets, setRealAssets] = useState([]);

  const getAccountOwnerPrompt = (accountResource) => {
    const foundOption = accountResource.meta()
      .field('ownerClientId')
      .options()
      .list()
      .find(option => option.id() === accountResource.attr('ownerClientId'));

    if (foundOption) {
      return foundOption.attr('prompt');
    }
    return '';
  };

  const refresh = useCallback(async () => {
    setLiabilities([]);
    setInvestmentAssets([]);
    setRealAssets([]);
    const accounts = await refreshAccounts(householdResource);
    if (accounts) {
      const {
        liabilities: l,
        investmentAssets: ia,
        realAssets: ra,
      } = accounts;
      setLiabilities(l);
      setInvestmentAssets(ia);
      setRealAssets(ra);
    }
  }, [householdResource?.id()]);

  useRefreshAccounts(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    dispatchAccountsRefresh();
  }, [householdResource?.id()]);

  const headerRow = (label) => (
    <div style={{ width: '100%' }}>
      <div className={classes.titleRow}>
        <Typography
          variant="h3"
          color="primary"
          noWrap
          className={classes.label}
        >
          {label}
        </Typography>
        {label === 'Real Assets' ? <AddRealAssetButton label={label} /> : null}
      </div>
      <div className={classNames(classes.row, classes.headerRow)}>
        <Typography variant="h6" noWrap className={classes.name}>
          Account Name
        </Typography>
        <Typography variant="h6" noWrap className={classes.name}>
          Owner
        </Typography>
        <Typography variant="h6" noWrap className={classes.name}>
          Sync Status
        </Typography>
        <Typography variant="h6" className={classes.balance}>
          Balance
        </Typography>
      </div>
    </div>
  );

  const totalRow = (accounts, label, liability) => {
    let tot = 0;
    accounts.forEach((acc) => {
      const amount = logicalBalance(acc);
      tot += amount;
    });
    return (
      <div className={classNames(classes.row, classes.footerRow)}>
        <Typography variant="h6" nowrap className={classes.name}>
          {label}
        </Typography>
        <Typography variant="h6" nowrap className={classes.name} />
        <Typography variant="h6" nowrap className={classes.name} />
        <Typography
          variant="h6"
          className={classNames(classes.balance, {
            [classes.liability]: liability,
          })}
        >
          {currency(tot)}
        </Typography>
      </div>
    );
  };

  const TableRow = ({
    resource,
    liability,
    realAsset,
    dark,
  }) => (
    <div className={classNames(classes.row, { [classes.darkRow]: dark })}>
      <Typography variant="h6" noWrap className={classes.name}>
        {realAsset
          ? addSpacesAndCapitalize(resource.attr('name') || '<No Name>')
          : displayName(resource)}
      </Typography>
      <Typography variant="h6" noWrap className={classes.name}>
        {getAccountOwnerPrompt(resource)}
      </Typography>
      <Typography variant="h6" noWrap className={classes.name}>
        {resource.attr('externalAccountId') || resource.attr('isManaged') === 'true' ? (
          <AccountStatus
            className={classes.accountLink}
            resource={resource}
          />
        ) : (
          'N/A'
        )}
      </Typography>
      <Typography
        variant="h6"
        className={classNames(classes.balance, {
          [classes.liability]: liability,
        })}
      >
        {realAsset ? currency(resource.attr('value')) : currency(resource.attr('balance'))}
      </Typography>
      <IconButton
        aria-label="edit"
        size="small"
        className={classes.smaller}
        onClick={() => {
          if (realAsset) {
            const realAssetQuery = {};
            realAssetQuery[REAL_ASSET_QUERY_VALUE_ID] = resource.id();
            realAssetQuery[REAL_ASSET_QUERY_VALUE_TYPE] = resource.attr('accountType');
            realAssetQuery[REAL_ASSET_QUERY_VALUE_WITH_DELETE] = 'true';
            openModal(buildModalQuery(modalConsts.realAssetModal, realAssetQuery));
          } else {
            resource.withRelationship(
              fr.relationships.edit.name,
              (rel) => openURLInNewTab(rel.link('related').url()),
              () => null,
            );
          }
        }}
      >
        <EditIcon className={classes.smaller} />
      </IconButton>
    </div>
  );

  TableRow.propTypes = {
    resource: PropTypes.object.isRequired,
    liability: PropTypes.bool,
    realAsset: PropTypes.bool,
    dark: PropTypes.bool,
  };

  TableRow.defaultProps = {
    liability: false,
    realAsset: false,
    dark: false,
  };
  return (
    <>
      {headerRow('Investment Assets')}
      {investmentAssets.map((account, i) => (
        <TableRow resource={account} dark={i % 2 !== 0} />
      ))}
      {totalRow(investmentAssets, 'Investment Asset Total:')}
      <hr />
      {headerRow('Real Assets')}
      {realAssets.map((account, i) => (
        <TableRow resource={account} realAsset dark={i % 2 !== 0} />
      ))}
      {totalRow(realAssets, 'Real Asset Total:')}
      <hr />
      {headerRow('Liabilities')}
      {liabilities.map((account, i) => (
        <TableRow resource={account} liability dark={i % 2 !== 0} />
      ))}
      {totalRow(liabilities, 'Liability Total:', true)}
    </>
  );
}
